// Security validation utilities

export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  if (email.length > 320) {
    return { isValid: false, error: 'Email address is too long' };
  }
  
  return { isValid: true };
};

export const validatePassword = (password: string): { isValid: boolean; error?: string } => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  
  // SECURITY: Require minimum 6 characters
  if (password.length < 6) {
    return { isValid: false, error: 'Password must be at least 6 characters' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long (max 128 characters)' };
  }
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  
  // Only require uppercase and lowercase (numbers and special chars are optional)
  if (!hasUpperCase || !hasLowerCase) {
    return { 
      isValid: false, 
      error: 'Password must contain at least one uppercase and one lowercase letter' 
    };
  }
  
  // Check for common weak patterns
  if (/(.)\1{2,}/.test(password)) {
    return { isValid: false, error: 'Password should not contain 3 or more repeated characters' };
  }
  
  if (/123456|654321|abcdef|qwerty|password|admin|login/i.test(password)) {
    return { isValid: false, error: 'Password contains common sequences that are easily guessed' };
  }
  
  return { isValid: true };
};

export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  if (!username) {
    return { isValid: false, error: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters' };
  }
  
  if (username.length > 30) {
    return { isValid: false, error: 'Username must be less than 30 characters' };
  }
  
  // Allow alphanumeric, underscores, and hyphens
  const usernameRegex = /^[a-zA-Z0-9_-]+$/;
  if (!usernameRegex.test(username)) {
    return { 
      isValid: false, 
      error: 'Username can only contain letters, numbers, underscores, and hyphens' 
    };
  }
  
  // Prevent reserved words
  const reservedWords = ['admin', 'moderator', 'system', 'support', 'help', 'chess', 'bot'];
  if (reservedWords.includes(username.toLowerCase())) {
    return { isValid: false, error: 'This username is reserved' };
  }
  
  return { isValid: true };
};

export const validateDisplayName = (displayName: string): { isValid: boolean; error?: string } => {
  if (!displayName) {
    return { isValid: false, error: 'Display name is required' };
  }
  
  if (displayName.length > 50) {
    return { isValid: false, error: 'Display name must be less than 50 characters' };
  }
  
  // Basic sanitization - no control characters
  const hasControlChars = /[\x00-\x1F\x7F]/.test(displayName);
  if (hasControlChars) {
    return { isValid: false, error: 'Display name contains invalid characters' };
  }
  
  return { isValid: true };
};

export const validateRoomCode = (roomCode: string): { isValid: boolean; error?: string } => {
  if (!roomCode) {
    return { isValid: false, error: 'Room code is required' };
  }
  
  if (roomCode.length !== 6) {
    return { isValid: false, error: 'Room code must be exactly 6 characters' };
  }
  
  // Allow alphanumeric characters only
  const roomCodeRegex = /^[A-Z0-9]+$/;
  if (!roomCodeRegex.test(roomCode.toUpperCase())) {
    return { isValid: false, error: 'Room code can only contain letters and numbers' };
  }
  
  return { isValid: true };
};

export const validateRoomName = (roomName: string): { isValid: boolean; error?: string } => {
  if (!roomName) {
    return { isValid: false, error: 'Room name is required' };
  }
  
  if (roomName.length > 100) {
    return { isValid: false, error: 'Room name must be less than 100 characters' };
  }
  
  // Basic sanitization
  const hasControlChars = /[\x00-\x1F\x7F]/.test(roomName);
  if (hasControlChars) {
    return { isValid: false, error: 'Room name contains invalid characters' };
  }
  
  return { isValid: true };
};

export const sanitizeInput = (input: string): string => {
  // Remove control characters and excessive whitespace
  return input.replace(/[\x00-\x1F\x7F]/g, '').trim().replace(/\s+/g, ' ');
};

// Rate limiting utilities
const rateLimitMap = new Map<string, { count: number; lastReset: number }>();

export const checkRateLimit = (
  key: string, 
  maxAttempts: number = 5, 
  windowMs: number = 60000
): { allowed: boolean; remainingAttempts: number } => {
  const now = Date.now();
  const entry = rateLimitMap.get(key);
  
  if (!entry || now - entry.lastReset > windowMs) {
    rateLimitMap.set(key, { count: 1, lastReset: now });
    return { allowed: true, remainingAttempts: maxAttempts - 1 };
  }
  
  if (entry.count >= maxAttempts) {
    return { allowed: false, remainingAttempts: 0 };
  }
  
  entry.count++;
  return { allowed: true, remainingAttempts: maxAttempts - entry.count };
};

// Security logging utility
export const logSecurityEvent = (
  event: string, 
  userId?: string, 
  details?: Record<string, any>
) => {
  // In a production environment, this would send to a logging service
  console.log('Security Event:', {
    event,
    userId,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    ...details
  });
};